const ATTRIBUTE_CONTENT_SLIDER_ELEMENT = 'data-klt-c-content-slider';
const ATTRIBUTE_CONTROLS_ELEMENT = 'data-klt-c-content-slider-controls';
const ATTRIBUTE_CONTENT_SLIDER_ITEM_WRAPPER_ELEMENT = 'data-klt-c-content-slider-items';
const ATTRIBUTE_CONTENT_SLIDER_ITEM_ELEMENT = 'data-klt-c-content-slider-item';
const ATTRIBUTE_CONTENT_SLIDER_CONTROL_PREVIOUS = 'data-klt-c-content-slider-control-previous';
const ATTRIBUTE_CONTENT_SLIDER_CONTROL_NEXT = 'data-klt-c-content-slider-control-next';
const ATTRIBUTE_CONTENT_SLIDER_CONTROL_REWIND = 'data-klt-c-content-slider-control-rewind';
const ATTRIBUTE_CONTENT_SLIDER_CURRENT_INDEX = 'data-klt-c-content-slider-current-index';
const ATTRIBUTE_CONTENT_SLIDER_CONTROL_TEXT = 'data-klt-c-content-slider-control-text';
const ATTRIBUTE_CONTENT_SLIDER_CONTROL_SLIDE_PREVIEW = 'data-klt-c-content-slider-control-slide-preview';
const ATTRIBUTE_CONTENT_SLIDER_CONTROL_SLIDE_LIST = 'data-klt-c-content-slider-slide-list';

type Controls = {
    next: HTMLButtonElement;
    previous: HTMLButtonElement;
    rewind: HTMLButtonElement;
    list: HTMLButtonElement[];
};

type Slider = {
    controls: Controls;
    root: HTMLElement;
    itemWrapper: HTMLElement;
    slides: HTMLElement[];
};

export default function init() {
    const contentSliders = document.querySelectorAll(`[${ATTRIBUTE_CONTENT_SLIDER_ELEMENT}]`);
    contentSliders.forEach(initSlider);
}

/**
 * Init JavaScript slider functionality on given root element
 */
function initSlider(root: HTMLElement) {
    try {
        const itemWrapper = root.querySelector(`[${ATTRIBUTE_CONTENT_SLIDER_ITEM_WRAPPER_ELEMENT}]`) as HTMLElement;
        const slidesNodeList = itemWrapper.querySelectorAll(
            `[${ATTRIBUTE_CONTENT_SLIDER_ITEM_ELEMENT}]`
        ) as NodeListOf<HTMLElement>;
        const slides = Array.from(slidesNodeList);

        const controlsWrapper = root.querySelector(`[${ATTRIBUTE_CONTROLS_ELEMENT}]`);
        const controlPrevious = controlsWrapper.querySelector(
            `[${ATTRIBUTE_CONTENT_SLIDER_CONTROL_PREVIOUS}]`
        ) as HTMLButtonElement;
        const controlNext = controlsWrapper.querySelector(
            `[${ATTRIBUTE_CONTENT_SLIDER_CONTROL_NEXT}]`
        ) as HTMLButtonElement;
        const controlRewind = controlsWrapper.querySelector(
            `[${ATTRIBUTE_CONTENT_SLIDER_CONTROL_REWIND}]`
        ) as HTMLButtonElement;

        const controlListWrapper = controlsWrapper.querySelector(`[${ATTRIBUTE_CONTENT_SLIDER_CONTROL_SLIDE_LIST}]`);
        const controlListTemplateNode = controlListWrapper.querySelector(`li`).cloneNode(true);

        controlListWrapper.innerHTML = '';
        const controlList = slides.map((slide, index) => {
            const controlListItem = controlListTemplateNode.cloneNode(true) as HTMLLIElement;
            const dotControl = controlListItem.querySelector('button') as HTMLButtonElement;

            dotControl.innerHTML = `<span>Slide ${index + 1}: ${slide.getAttribute(
                'data-klt-c-content-slider-item'
            )}<span>`;
            controlListWrapper.appendChild(controlListItem);

            return dotControl;
        });

        const controls: Controls = {
            previous: controlPrevious,
            next: controlNext,
            rewind: controlRewind,
            list: controlList,
        };

        const contentSlider = {
            controls,
            slides,
            root,
            itemWrapper,
        };

        if (contentSlider.slides[0]) {
            const firstSlideTitle = contentSlider.slides[0].getAttribute('data-klt-c-content-slider-item');
            contentSlider.controls.rewind.querySelector(
                `[${ATTRIBUTE_CONTENT_SLIDER_CONTROL_SLIDE_PREVIEW}]`
            ).textContent = firstSlideTitle;
        }

        initSlideChangeObserver(contentSlider);
        initEventHandlers(contentSlider);

        // JavaScript module initalized. Give signal to CSS to be able to apply different styles
        // based on whether JavaScript functionality is available or not.
        contentSlider.root.classList.add('js');
        controlsWrapper.removeAttribute('hidden');
    } catch (e) {
        console.error("Couldn't initalize slider", e);
    }
}

/**
 * Initalizing this function triggers an observer that updates the current index
 * when a slider is changed
 */
function initSlideChangeObserver(slider: Slider, observerSettings: IntersectionObserverInit = { threshold: 0.5 }) {
    // Note: IntersectionObserver isn't supported in IE natively
    if ('IntersectionObserver' in window) {
        const callback: IntersectionObserverCallback = function callback(entries, observer) {
            for (const entry of entries) {
                const focusableElements = entry.target.querySelectorAll('a');

                if (entry.isIntersecting) {
                    const target = entry.target as HTMLElement;
                    const elementIndex = slider.slides.indexOf(target);

                    updateControls(elementIndex, slider);
                    setCurrentIndex(elementIndex, slider);
                    entry.target.classList.add('visible');
                    focusableElements.forEach((element) => element.removeAttribute('tabindex'));
                } else {
                    entry.target.classList.remove('visible');
                    focusableElements.forEach((element) => element.setAttribute('tabindex', '-1'));
                }
            }
        };

        var observer = new IntersectionObserver(callback, observerSettings);
        Array.prototype.forEach.call(slider.slides, function (t) {
            return observer.observe(t);
        });

        updateControls(0, slider);
        setCurrentIndex(0, slider);
    }
}

/**
 * Init event handlers like next/previous clicks
 */
function initEventHandlers(slider: Slider) {
    slider.controls.previous.addEventListener('click', () => {
        const currentItemIndex = getCurrentIndex(slider);
        scrollToIndex(currentItemIndex - 1, slider);
    });

    slider.controls.next.addEventListener('click', () => {
        const currentItemIndex = getCurrentIndex(slider);
        scrollToIndex(currentItemIndex + 1, slider);
    });

    slider.controls.rewind.addEventListener('click', () => {
        scrollToIndex(0, slider);
    });

    slider.controls.list.forEach((dotControl, index) =>
        dotControl.addEventListener('click', () => {
            scrollToIndex(index, slider);
        })
    );
}

/**
 * Update texts and visibility of the slider controls
 */
function updateControls(currentItemIndex: number, slider: Slider) {
    const isFirstItem = currentItemIndex === 0;
    const isLastItem = slider.itemWrapper.children.length - 1 === currentItemIndex;
    const previousSlide = slider.slides[currentItemIndex - 1] as HTMLElement;
    const nextSlide = slider.slides[currentItemIndex + 1] as HTMLElement;

    if (isFirstItem) {
        slider.controls.previous.setAttribute('aria-hidden', 'true');
        slider.controls.previous.disabled = true;

        slider.controls.next.removeAttribute('aria-hidden');
        slider.controls.next.disabled = false;
        slider.controls.next.hidden = false;

        slider.controls.rewind.setAttribute('aria-hidden', 'true');
        slider.controls.rewind.disabled = true;
        slider.controls.rewind.hidden = true;

        if (document.activeElement === slider.controls.previous) {
            slider.controls.next.focus();
        }
    } else if (isLastItem) {
        let fnFocus = () => {};
        if (document.activeElement === slider.controls.next) {
            fnFocus = () => slider.controls.rewind.focus();
        }

        slider.controls.previous.removeAttribute('aria-hidden');
        slider.controls.previous.disabled = false;

        slider.controls.next.setAttribute('aria-hidden', 'true');
        slider.controls.next.disabled = true;
        slider.controls.next.hidden = true;

        slider.controls.rewind.removeAttribute('aria-hidden');
        slider.controls.rewind.disabled = false;
        slider.controls.rewind.hidden = false;

        fnFocus();
    } else {
        slider.controls.previous.removeAttribute('aria-hidden');
        slider.controls.previous.disabled = false;

        slider.controls.next.removeAttribute('aria-hidden');
        slider.controls.next.disabled = false;
        slider.controls.next.hidden = false;

        slider.controls.rewind.setAttribute('aria-hidden', 'true');
        slider.controls.rewind.disabled = true;
        slider.controls.rewind.hidden = true;
    }

    if (previousSlide) {
        const previousSlideTitle = previousSlide.getAttribute('data-klt-c-content-slider-item');
        slider.controls.previous.querySelector(`[${ATTRIBUTE_CONTENT_SLIDER_CONTROL_SLIDE_PREVIEW}]`).textContent =
            previousSlideTitle;
    }

    if (nextSlide) {
        const nextSlideTitle = nextSlide.getAttribute('data-klt-c-content-slider-item');
        slider.controls.next.querySelector(`[${ATTRIBUTE_CONTENT_SLIDER_CONTROL_SLIDE_PREVIEW}]`).textContent =
            nextSlideTitle;
    }

    slider.controls.list.forEach((item, i) => {
        if (i === currentItemIndex) {
            item.setAttribute('aria-selected', 'true');
        } else {
            item.removeAttribute('aria-selected');
        }
    });
}

/**
 * Set the item of the given slider to the given index
 */
function scrollToIndex(index: number, slider: Slider) {
    const scrollPosition = index * (slider.itemWrapper.scrollWidth / slider.slides.length);
    slider.itemWrapper.scrollLeft = scrollPosition;
    setCurrentIndex(index, slider);
}

/**
 * Save new index to content slider
 */
function setCurrentIndex(index: number, slider: Slider): void {
    slider.root.setAttribute(ATTRIBUTE_CONTENT_SLIDER_CURRENT_INDEX, index.toString());
}

/**
 * Get current index from content slider
 */
function getCurrentIndex(slider: Slider): number {
    return parseInt(slider.root.getAttribute(ATTRIBUTE_CONTENT_SLIDER_CURRENT_INDEX));
}
